import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types';
// @ts-ignore
import SurveyConnectLogo from '../assets/survey_connect_logo.png';

// Auth screens
import { LoginScreen } from '../screens/auth/LoginScreen';
import { RegisterScreen } from '../screens/auth/RegisterScreen';
import { OtpScreen } from '../screens/auth/OTPScreen';

// Shared
import { ZimbabweGatePage } from '../screens/shared/ZimbabweGatePage';
import { NotFoundScreen } from '../screens/shared/NotFoundScreen';

// Layouts
import { DashboardLayout } from '../layouts/DashboardLayout';

// Client screens
import { ClientCategories } from '../screens/client/ClientCategoriesWeb';
import { ClientDashboard } from '../screens/client/ClientDashboard';
import { CreateRequestScreen } from '../screens/client/CreateRequestScreenWeb';
import { RequestDetailScreen } from '../screens/client/RequestDetailScreenWeb';
import { ClientChatScreen } from '../screens/client/ClientChatScreen';
import { ClientMessages } from '../screens/client/ClientMessagesWeb';
import { ClientChatRoom } from '../screens/client/ClientChatRoomScreen';
import { DealConfirmationScreen } from '../screens/client/DealConfirmationScreenWeb';
import { RatingScreen } from '../screens/shared/RatingScreenWeb';
import { PortalMapScreen } from '../screens/shared/PortalMapScreen';
import { ClientSurveyHistory } from '../screens/client/ClientSurveyHistoryWeb';
import { SurveyorPublicProfile } from '../screens/client/SurveyorPublicProfileWeb';

// Surveyor screens
import { SurveyorBrowseJobs } from '../screens/surveyor/SurveyorBrowseJobsWeb';
import { SurveyorAdvertiseJob } from '../screens/surveyor/SurveyorAdvertiseJobWeb';
import { SurveyorOnboarding } from '../screens/surveyor/SurveyorOnboarding';
import { SurveyorDashboard } from '../screens/surveyor/SurveyorDashboardWeb';
import { SurveyorDealDetail } from '../screens/surveyor/SurveyorDealDetailWeb';
import { SurveyorRequestDetail } from '../screens/surveyor/SurveyorRequestDetailWeb';
import { SurveyorChatScreen } from '../screens/surveyor/SurveyorChatScreen';
import { SurveyorMessages } from '../screens/surveyor/SurveyorMessages';
import { SurveyorChatRoom } from '../screens/surveyor/SurveyorChatRoomScreen';
import { SurveyorProfile } from '../screens/surveyor/SurveyorProfileWeb';
import { SurveyorSurveyHistory } from '../screens/surveyor/SurveyorSurveyHistoryWeb';

// Admin screens
import { AdminJobLeads } from '../screens/admin/AdminJobLeadsWeb';
import { AdminCategories } from '../screens/admin/AdminCategoriesWeb';
import { AdminOverview } from '../screens/admin/AdminOverviewWeb';
import { AdminRequestsReview } from '../screens/admin/AdminRequestsReviewWeb';
import { AdminRequestDetail } from '../screens/admin/AdminRequestDetailWeb';
import { AdminNegotiations } from '../screens/admin/AdminNegotiationsWeb';
import { AdminNegotiationDetail } from '../screens/admin/AdminNegotiationDetailWeb';
import { AdminDeals } from '../screens/admin/AdminDealsWeb';
import { AdminClients } from '../screens/admin/AdminClientsWeb';
import { AdminSurveyors } from '../screens/admin/AdminSurveyorsWeb';
import { AdminSurveyorDetail } from '../screens/admin/AdminSurveyorDetailWeb';
import { AdminAuditLogs } from '../screens/admin/AdminAuditLogsWeb';
import { AdminSettings } from '../screens/admin/AdminSettingsWeb';
import { AdminSurveyHistory } from '../screens/admin/AdminSurveyHistoryWeb';
import { AdminChatOversight } from '../screens/admin/AdminChatOversightWeb';
import { AdminChatRoom } from '../screens/admin/AdminChatRoomWeb';
import { AdminAntiCheat } from '../screens/admin/AdminAntiCheatWeb';

declare const __APP_ROUTER_BASENAME__: string | undefined;

const routerBasename = typeof __APP_ROUTER_BASENAME__ !== 'undefined'
  ? __APP_ROUTER_BASENAME__
  : '';

/* ── Zimbabwe geo-gate (runs once per session) ── */
const GeoGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (sessionStorage.getItem('zw_gate_passed')) return; // already cleared this session
    if (!navigator.geolocation) {
      navigate('/zimbabwe-gate', { replace: true, state: { returnTo: location.pathname } });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        // Zimbabwe bounding box
        const inside = lat >= -22.5 && lat <= -15.3 && lng >= 25.2 && lng <= 33.1;
        if (inside) {
          sessionStorage.setItem('zw_gate_passed', '1');
        } else {
          navigate('/zimbabwe-gate', { replace: true, state: { returnTo: location.pathname } });
        }
      },
      () => {
        navigate('/zimbabwe-gate', { replace: true, state: { returnTo: location.pathname } });
      },
      { timeout: 8000 },
    );
  }, []);

  return <>{children}</>;
};

/* ── Auth guard ─────────────────────────────────── */
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuthStore();
  if (!isInitialized) return <SplashLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

/* ── Role guard ─────────────────────────────────── */
const RequireRole: React.FC<{ role: UserRole; children: React.ReactNode }> = ({ role, children }) => {
  const { user } = useAuthStore();
  if (user?.role !== role) return <Navigate to={roleHome(user?.role)} replace />;
  return <>{children}</>;
};

/* ── Guest guard (redirect auth users away from login) ── */
const GuestOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitialized, user } = useAuthStore();
  if (!isInitialized) return <SplashLoader />;
  if (isAuthenticated) return <Navigate to={roleHome(user?.role)} replace />;
  return <>{children}</>;
};

/* helpers */
function roleHome(role?: UserRole | null): string {
  switch (role) {
    case UserRole.ADMIN: return '/admin';
    case UserRole.SURVEYOR: return '/surveyor';
    default: return '/client';
  }
}

const SplashLoader: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0A0E12', gap: 28 }}>
    <img src={SurveyConnectLogo} alt="Survey Connect" style={{ width: 200, height: 56, objectFit: 'contain', filter: 'var(--sc-logo-filter)' }} />
    <div style={{ width: 32, height: 32, border: '3px solid #1E293B', borderTopColor: '#0C5ADB', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <span style={{ fontSize: 13, color: '#64748B', fontWeight: 500 }}>Loading Survey Connect…</span>
  </div>
);

/* ══════ Router ═══════════════════════════════════ */
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter basename={routerBasename || undefined}>
      <GeoGate>
        <Routes>
        {/* ── Public ── */}
        <Route path="/login" element={<GuestOnly><LoginScreen /></GuestOnly>} />
        <Route path="/register" element={<GuestOnly><RegisterScreen /></GuestOnly>} />
        <Route path="/verify-otp" element={<GuestOnly><OtpScreen /></GuestOnly>} />
        <Route path="/zimbabwe-gate" element={<ZimbabweGatePage />} />

        {/* ── Client ── */}
        <Route path="/client" element={<RequireAuth><RequireRole role={UserRole.CLIENT}><DashboardLayout /></RequireRole></RequireAuth>}>
          <Route index element={<ClientDashboard />} />
          <Route path="request/create" element={<CreateRequestScreen />} />
          <Route path="request/:id" element={<RequestDetailScreen />} />
          <Route path="categories" element={<ClientCategories />} />
          <Route path="map" element={<PortalMapScreen />} />
          <Route path="chat/:negotiationId" element={<ClientChatScreen />} />
          <Route path="messages" element={<ClientMessages />} />
          <Route path="chat/room/:conversationId" element={<ClientChatRoom />} />
          <Route path="deal/:dealId" element={<DealConfirmationScreen />} />
          <Route path="rate/:dealId" element={<RatingScreen />} />
          <Route path="history" element={<ClientSurveyHistory />} />
          <Route path="surveyor/:surveyorId" element={<SurveyorPublicProfile />} />
        </Route>

        {/* ── Surveyor ── */}
        <Route path="/surveyor" element={<RequireAuth><RequireRole role={UserRole.SURVEYOR}><DashboardLayout /></RequireRole></RequireAuth>}>
          <Route index element={<SurveyorDashboard />} />
          <Route path="browse-jobs" element={<SurveyorBrowseJobs />} />
          <Route path="map" element={<PortalMapScreen />} />
          <Route path="onboarding" element={<SurveyorOnboarding />} />
          <Route path="request/:requestId" element={<SurveyorRequestDetail />} />
          <Route path="deal/:dealId" element={<SurveyorDealDetail />} />
          <Route path="chat/:negotiationId" element={<SurveyorChatScreen />} />
          <Route path="messages" element={<SurveyorMessages />} />
          <Route path="chat/room/:conversationId" element={<SurveyorChatRoom />} />
          <Route path="profile" element={<SurveyorProfile />} />
          <Route path="history" element={<SurveyorSurveyHistory />} />
          <Route path="advertise-jobs" element={<SurveyorAdvertiseJob />} />
        </Route>

        {/* ── Admin ── */}
        <Route path="/admin" element={<RequireAuth><RequireRole role={UserRole.ADMIN}><DashboardLayout /></RequireRole></RequireAuth>}>
          <Route index element={<AdminOverview />} />
          <Route path="requests" element={<AdminRequestsReview />} />
          <Route path="map" element={<PortalMapScreen />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="requests/:id" element={<AdminRequestDetail />} />
          <Route path="negotiations" element={<AdminNegotiations />} />
          <Route path="negotiations/:id" element={<AdminNegotiationDetail />} />
          <Route path="deals" element={<AdminDeals />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="surveyors" element={<AdminSurveyors />} />
          <Route path="surveyors/:id" element={<AdminSurveyorDetail />} />
          <Route path="audit-logs" element={<AdminAuditLogs />} />
          <Route path="chat-oversight" element={<AdminChatOversight />} />
          <Route path="anti-cheat" element={<AdminAntiCheat />} />
          <Route path="chat/room/:conversationId" element={<AdminChatRoom />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="history" element={<AdminSurveyHistory />} />
          <Route path="job-leads" element={<AdminJobLeads />} />
        </Route>

        {/* ── Fallback ── */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
      </GeoGate>
    </BrowserRouter>
  );
};
